import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getStepsByGoal, updateSavingsPlan } from "../../redux/actions";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import theme from "../../theme/theme";
import Input from "../Base/Input";
import { Select } from "../Base/Select/index";
import Card from "../Base/Card";
import Tooltip from "../Base/Tooltip";
import {
  getGoalPathFromNumber,
  mapNumberToType,
  validateField,
} from "../../utils";
import ConfirmationModal from "../ConfirmationModal";
import RefreshActive from "../../assets/refresh-active.svg";
import Refresh from "../../assets/refresh.svg";
import usePrevious from "../../hooks/usePrevious";
import CustomExpandCollapseButton from "../CustomExpandCollapseButton";
import { analytics } from "../../analytics";

const Wrapper = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  padding: 30px 20px;
  background-color: ${theme.colors.white};

  .paddingForMobile {
    padding: 5% 5%;
    margin-bottom: 0px !important;
  }

  @media (max-width: 480px) {
    padding: 0% 0%;
    gap: 20px;
  }

  .goalSection,
  .settingsSection {
    color: ${theme.colors.primaryStrong};
    font-size: 1.75em;
    line-height: 1.29;
  }

  .goalSection {
    display: flex;
    align-items: center;

    @media (max-width: 480px) {
      font-size: 20px;
    }
  }

  .settingsSection {
    display: flex;
    align-items: center;
    width: 100%;

    .refreshIcon {
      border: none;
      background-color: transparent;
      margin-left: auto;
      background-position: center;
      background-repeat: no-repeat;
      background-size: 48px;
      width: 48px;
      height: 48px;
      background-image: url("${Refresh}");

      &.active {
        background-image: url("${RefreshActive}");
      }
    }
  }

  > div:not(:last-child) {
    margin-bottom: 20px;
  }

  .paddingInMobile {
    width: 100%;
    gap: 20px;
    display: flex;
    flex-direction: column;
    @media (max-width: 480px) {
      padding: 5% 5%;
      width: 90%;
      gap: 20px;
      display: flex;
      flex-direction: column;
    }
  }

  .goalAchievability {
    font-size: 38px;
    line-height: 47px;
    background-color: ${theme.colors.white};
    color: ${theme.colors.primaryStrong};
  }

  input[disabled] {
    cursor: not-allowed;
  }
`;
const goalsOptions = [
  { label: "Retirement", value: 1, selected: false },
  { label: "Major Purchase", value: 2, selected: true },
  { label: "Education", value: 3, selected: false },
  { label: "Build Wealth", value: 4, selected: false },
];

const riskProfileOptions = [
  { label: "Very Conservative", value: 1 },
  { label: "Conservative", value: 2 },
  { label: "Balanced", value: 3 },
  { label: "Adventurous", value: 4 },
  { label: "Very Adventurous", value: 5 },
];

const goalNames = ["Retirement", "Major Purchase", "Education", "Build Wealth"];

const GoalSettings = ({ data, setBtnDisabled }) => {
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const inputQns = data?.settings?.additionalInfos || [];
  const history = useHistory();
  const dispatch = useDispatch();
  const { savingsPlanId, dashboardData, goal } = useSelector(
    (state) => state.user
  );
  const [showModal, setShowModal] = useState(false);
  const [goalState, setGoal] = useState({});
  const [goalLabel, setGoalLabel] = useState(data?.yourGoal?.goal);
  const [activateRefresh, setActivateRefresh] = useState(false);
  const [riskProfile, setRiskProfile] = useState(0);
  const prevGoal = usePrevious(data?.yourGoal?.goal);
  const [inputs, setInputs] = useState(
    inputQns.map((question) => ({ ...question, disabled: false })) || []
  );

  useEffect(() => {
    setInputs(
      inputQns.map((question) => ({ ...question, disabled: false })) || []
    );
    setErrors(
      inputQns.map((question) => ({
        inputId: question.savingsPlanAdditionalInfoId,
        errorMessage: "",
      }))
    );
  }, [inputQns]);

  useEffect(() => {
    if (data) {
      setRiskProfile(
        data?.settings?.additionalInfos.filter(
          (data) => data.subject.indexOf("Risk Profile") > -1
        )[0].value
      );
    }
    setGoalLabel(data?.yourGoal?.goal || 1);
    setSelectDisabled(false);
  }, [data]);

  const [errors, setErrors] = useState(
    inputQns.map((question) => ({
      inputId: question.savingsPlanAdditionalInfoId,
      errorMessage: "",
    }))
  );

  const [changedInputName, setChangedInputName] = useState("");
  const [selectDisabled, setSelectDisabled] = useState(false);

  const setInputsDisabledExcept = (inputId = null) => {
    setInputs((prevInput) =>
      prevInput.map((question) => ({
        ...question,
        disabled:
          question.savingsPlanAdditionalInfoId !== inputId ? true : false,
      }))
    );
  };

  const handleInputChange = useCallback(
    (e, questionId, inputName, minValue, maxValue) => {
      if (isNaN(e.target.value)) return "0";

      const errMsg = validateField(minValue, maxValue, e.target.value);
      setInputs((prevVals) =>
        prevVals.map((val) =>
          val.savingsPlanAdditionalInfoId === questionId
            ? { ...val, value: e.target.value }
            : val
        )
      );

      setErrors((prevErrs) =>
        prevErrs.map((err) =>
          err.inputId === questionId ? { ...err, errorMessage: errMsg } : err
        )
      );

      setChangedInputName(inputName);
      setActivateRefresh(!errMsg);
      setBtnDisabled(true);
      setInputsDisabledExcept(questionId);
      setSelectDisabled(true);
    },
    [setInputs, setBtnDisabled]
  );

  const onRiskProfileChange = useCallback(
    (option) => {
      setRiskProfile(option);
      setActivateRefresh(true);
      setBtnDisabled(true);
      setInputsDisabledExcept();
      setChangedInputName("Risk Profile");
    },
    [setBtnDisabled]
  );

  if (!data) return null;
  // const { yourGoal } = data;

  const onGoalChange = (option) => {
    const path = getGoalPathFromNumber(option);
    const type = mapNumberToType(option);
    setGoal({ type, path });
    setShowModal(true);
  };

  const onConfirm = () => {
    const today = new Date();
    dispatch(getStepsByGoal(goalState)).then(() => {
      analytics.track("Goal Changed", {
        goalId: goal?.id || "",
        goalType: goalState?.type || "",
        previousGoalType: goalNames[data?.yourGoal?.goal - 1],
        questionnaireUpdated: today.toLocaleString(),
        goalRisk:
          getGoalData(dashboardData?.settings?.additionalInfos).goalRisk || "",
        goalMonthly:
          getGoalData(dashboardData?.settings?.additionalInfos).goalMonthly ||
          "",
        goalTime:
          getGoalData(dashboardData?.settings?.additionalInfos).goalTime || "",
        goalInitial:
          getGoalData(dashboardData?.settings?.additionalInfos).goalInitial ||
          "",
        goalAmount:
          getGoalData(dashboardData?.settings?.additionalInfos).goalAmount ||
          "",
      });
      history.push(goalState.path);
    });
  };

  const updateDashboardData = () => {
    const selectedStrategy = dashboardData?.strategies.find(
      (stgy) => stgy.isSelected
    );
    const updateData = {
      savingsPlanId,
      strategies: [selectedStrategy.id],
      changedParam: changedInputName,
      settingsData: {},
    };

    const riskProfileData = {
      ...data?.settings?.additionalInfos[0],
      value: riskProfile,
    };

    const inputsExceptRiskProfile = inputs.filter(
      (data) => data.subject !== "Risk Profile"
    );
    updateData.settingsData.additionalInfos = [
      riskProfileData,
      ...inputsExceptRiskProfile,
    ];

    dispatch(updateSavingsPlan({ data: updateData })).then(() => {
      setBtnDisabled(false);
    });

    analytics.track("Goal Updated", {
      goalId: goal?.id || "",
      goalType: goalNames[data?.yourGoal?.goal - 1],
      goalRisk:
        getGoalData(updateData?.settingsData?.additionalInfos).goalRisk || "",
      goalMonthly:
        getGoalData(updateData?.settingsData?.additionalInfos).goalMonthly ||
        "",
      goalTime:
        getGoalData(updateData?.settingsData?.additionalInfos).goalTime || "",
      goalInitial:
        getGoalData(updateData?.settingsData?.additionalInfos).goalInitial ||
        "",
      goalAmount:
        getGoalData(updateData?.settingsData?.additionalInfos).goalAmount || "",
    });
  };

  const getGoalData = (data) => {
    let goalTypeIndex, goalMonthly, goalTime, goalInitial, goalAmount;

    data.forEach((element) => {
      if (
        element.subject.replace(/[^\x00-\x7F]/g, "") === "Risk Profile" &&
        !goalTypeIndex
      ) {
        goalTypeIndex = element.value;
      }
      if (element.subject === "Monthly Contribution") {
        goalMonthly = element.value;
      }
      if (
        element.subject === "Years Shown" ||
        element.subject === "Years Remaining" ||
        element.subject === "Retirement Age"
      ) {
        goalTime = element.value;
      }
      if (element.subject === "Initial Deposit") {
        goalInitial = element.value;
      }
      if (
        element.subject === "Goal Amount" ||
        element.subject === "Retirement Income Goal"
      ) {
        goalAmount = element.value;
      }
    });

    const goalRisks = [
      "Very Conservative",
      "Conservative",
      "Balanced",
      "Adventurous",
      "Very Adventurous",
    ];
    return {
      goalRisk: goalRisks[goalTypeIndex - 1],
      goalMonthly: goalMonthly,
      goalTime: goalTime,
      goalInitial: goalInitial || "NA",
      goalAmount: goalAmount || "NA",
    };
  };

  return (
    <Wrapper>
      <div className="paddingForMobile">
        <span className="goalSection">
          Your Goal
          <Tooltip
            className={`${window.screen.width > 480 ? "left" : "right"}`}
            text="The first step to achieving a goal is setting one. Tactive recommends you create a goal or multiple goals for your investment strategy. From this screen, you can modify your risk tolerance, initial deposit, monthly contribution or years to achieve your goal. If you wish to create a new goal, select it from the dropdown menu."
          />
        </span>
      </div>
      {(isPanelExpanded || window.screen.width > 480) && (
        <div className="paddingInMobile">
          <Select
            options={goalsOptions}
            defaultValue={data?.yourGoal?.goal}
            onChange={onGoalChange}
            label="Select Goal"
            key={goalLabel}
          />
          {/* <Input
        className="goalAchievability"
        value={`${yourGoal?.goalAchievability || 0} %`}
        label="Goal Achievability"
        disabled
      /> */}
          <span className="settingsSection">
            Settings{" "}
            <Tooltip
              className="left"
              text="The Tactive platform is built so individuals have flexibility to see how investment projections might change by updating risk tolerance, initial deposit, monthly contribution or years before launching your investment. Simply change the numbers below and hit the refresh icon to update your personalized investment mix. 
              Note: Your risk profile is based on your feedback from the questionnaire and might not reflect your initial risk profile selection. To change your preferred risk tolerance, select from the list below and refresh your settings.
              "
            />
            <button
              onClick={updateDashboardData}
              className={`refreshIcon ${activateRefresh ? "active" : ""}`}
              disabled={!activateRefresh}
            ></button>
          </span>
          {riskProfile !== 0 && riskProfile !== undefined && (
            <Select
              options={riskProfileOptions}
              defaultValue={riskProfile}
              onChange={onRiskProfileChange}
              label="Risk Profile"
              disabled={selectDisabled}
            />
          )}
          {inputs?.map((settingsData) => {
            return settingsData.subject.indexOf("Risk Profile") <= -1 ? (
              <Input
                disabled={settingsData.disabled}
                key={settingsData.savingsPlanAdditionalInfoId}
                value={settingsData.value || null}
                label={settingsData.subject || "Label"}
                onChange={(e) =>
                  handleInputChange(
                    e,
                    settingsData.savingsPlanAdditionalInfoId,
                    settingsData.subject,
                    settingsData.minValue || 0,
                    settingsData.maxValue || 9999999
                  )
                }
                error={errors.find(
                  (errItem) =>
                    errItem.inputId === settingsData.savingsPlanAdditionalInfoId
                )}
              />
            ) : (
              <></>
            );
          })}
          <ConfirmationModal
            onClose={() => {
              setGoalLabel(prevGoal);
              setShowModal(false);
            }}
            show={showModal}
            onConfirm={onConfirm}
            text="Selecting a new goal will require you to restart the questionnaire"
          />
        </div>
      )}
      {window.screen.width < 480 && (
        <div style={{ width: "100%" }}>
          <CustomExpandCollapseButton
            toggleExpandCollapse={setIsPanelExpanded}
            isPanelExpanded={isPanelExpanded}
          />
        </div>
      )}
    </Wrapper>
  );
};

export default GoalSettings;
